/**
 * The permission matrix as the SPA reads it (design r7, Story 22).
 *
 * `GET /me` carries, per permission, where the caller holds it: `'all'`, a
 * list of Client tenant ids, or `false`. This only decides which controls
 * to show — the API refuses regardless — so it must agree with the server
 * but is never trusted in its place.
 */
import type { Me, Permission } from './types.js';

/**
 * Whether `me` holds `permission` on `tenantId`, or — with no tenant —
 * on at least one Client (the question a column header or a nav entry
 * asks). A null `me` holds nothing.
 */
export function can(me: Me | null | undefined, permission: Permission, tenantId?: string): boolean {
  if (permission === 'manage_org') return canManageOrg(me, tenantId);
  const grant = me?.permissions[permission];
  if (!grant) return false;
  if (grant === 'all') return true;
  return tenantId === undefined ? grant.length > 0 : grant.includes(tenantId);
}

/**
 * `manage_org` is held per node, not per Client (Story 48, ADR-0033 §3):
 * `/me` carries the home node's id, meaning "this node and below". With no
 * node, whether the caller holds it at all. With one, only the home node
 * itself is known here; descendants need the tree, which the SPA reads
 * from `/orgs` (task 208). Never a substring match on the id.
 */
function canManageOrg(me: Me | null | undefined, orgId?: string): boolean {
  const home = me?.permissions.manage_org;
  if (typeof home !== 'string') return false;
  return orgId === undefined || orgId === home;
}
